import React, { useState, useRef, useEffect } from 'react';
import styled from '@emotion/styled';
import { FaTimes, FaPrint, FaFolderOpen, FaTag, FaBox, FaFileInvoiceDollar, FaCheckCircle, FaClock, FaTruck, FaClipboardCheck, FaHistory } from 'react-icons/fa';
import FilesModal from './FilesModal';
import { sortSizes, getOrderStatusDisplay, LINE_TYPES, ORDER_STATUSES } from '../firebase/schema';
import { getFacility, resolveFacilityId, resolveHomeFacilityId, isAwayFromHome } from '../utils/facilities';
import { getMockupUrl } from '../utils/fileHelpers';
import { formatOrderPoNumber } from '../utils/orderPoNumber';
import { computeOrderDocumentTotals } from '../utils/orderTotals';
import JobSticker from './JobSticker';
import { formatDate } from '../utils/dateFormatter';
import BoxSticker from './BoxSticker';
import BoxCountDialog from './BoxCountDialog';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db, functions } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import { getEmailContent } from '../emailTemplates';
import { fetchStickerPrintPayload } from '../utils/fetchStickerPrintPayload';
import { printLabelBase64 } from '../utils/printServer';

// ============ STYLED COMPONENTS ============

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.85);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;

  @media (max-width: 768px) {
    background-color: var(--al-bg);
    align-items: stretch;
    justify-content: stretch;
  }
`;

const ModalContent = styled.div`
  background: var(--al-bg);
  width: 95%;
  max-width: 1200px;
  max-height: 85vh;
  height: auto;
  position: relative;
  font-family: 'Montserrat', sans-serif;
  color: white;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  @media (max-width: 768px) {
    width: 100%;
    max-width: 100%;
    height: 100vh;
    height: 100dvh;
    max-height: 100vh;
    max-height: 100dvh;
    padding-top: env(safe-area-inset-top);
    padding-bottom: env(safe-area-inset-bottom);
    box-sizing: border-box;
    overflow-x: hidden;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.6);
  cursor: pointer;
  font-size: 24px;
  z-index: 10;
  transition: color 0.2s;
  
  &:hover {
    color: var(--al-primary);
  }

  @media (max-width: 768px) {
    top: 12px;
    right: 12px;
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background: rgba(0, 0, 0, 0.5);
  }
`;

// ============ HEADER ============

const Header = styled.div`
  background: var(--al-surface);
  padding: 16px 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  flex-shrink: 0;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
    padding: 14px 56px 14px 16px; /* right padding leaves room for floating close button */
  }
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;

  @media (max-width: 768px) {
    flex-direction: row;
    align-items: center;
    gap: 12px;
    min-width: 0;
  }
`;

const OrderNumber = styled.div`
  background: black;
  color: white;
  padding: 12px 20px;
  flex-shrink: 0;
  
  .label {
    font-size: 0.65em;
    color: rgba(255, 255, 255, 0.6);
    letter-spacing: 0.1em;
    margin-bottom: 2px;
  }
  
  .number {
    font-size: 1.2em;
    font-weight: 700;
    letter-spacing: 0.05em;
  }

  @media (max-width: 768px) {
    padding: 8px 12px;

    .label {
      font-size: 0.55em;
      margin-bottom: 0;
    }

    .number {
      font-size: 1em;
    }
  }
`;

const CustomerInfo = styled.div`
  min-width: 0;
  flex: 1;

  .customer-name {
    font-size: 1.1em;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin-bottom: 2px;
  }
  
  .contact {
    font-size: 0.8em;
    color: rgba(255, 255, 255, 0.6);
  }

  @media (max-width: 768px) {
    .customer-name {
      font-size: 0.95em;
      letter-spacing: 0.02em;
      line-height: 1.2;
      overflow-wrap: break-word;
    }
  }
`;

const HeaderRight = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
  margin-right: 40px;

  @media (max-width: 768px) {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-right: 0;
    flex-wrap: wrap;
    width: 100%;
  }
`;

const StatusBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: ${props => {
    switch (props.status) {
      case 'Created': return 'rgba(255, 165, 0, 0.2)';
      case 'Ready To Order': return 'rgba(0, 255, 0, 0.2)';
      case 'Ordered': return 'rgba(30, 144, 255, 0.2)';
      case 'Received': return 'rgba(138, 43, 226, 0.2)';
      case 'Ready To Invoice': return 'rgba(255, 215, 0, 0.2)';
      case 'Completed': return 'rgba(var(--al-primary-rgb), 0.2)';
      default: return 'rgba(255, 255, 255, 0.1)';
    }
  }};
  color: ${props => {
    switch (props.status) {
      case 'Created': return '#FFA500';
      case 'Ready To Order': return '#00FF00';
      case 'Ordered': return '#1E90FF';
      case 'Received': return '#8A2BE2';
      case 'Ready To Invoice': return '#FFD700';
      case 'Completed': return 'var(--al-primary)';
      default: return 'white';
    }
  }};
  font-size: 0.85em;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;

  @media (max-width: 768px) {
    font-size: 0.75em;
    padding: 6px 12px;
  }
`;

const DueDate = styled.div`
  font-size: 0.85em;
  color: rgba(255, 255, 255, 0.6);
  
  span {
    color: white;
    font-weight: 500;
  }

  @media (max-width: 768px) {
    font-size: 0.8em;
  }
`;

// ============ MAIN CONTENT ============

const MainLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr 300px;
  flex: 1;
  min-height: 0;
  overflow: hidden;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    overflow-y: auto;
  }
`;

const LinesSection = styled.div`
  padding: 20px;
  overflow-y: auto;
  border-right: 1px solid rgba(255, 255, 255, 0.1);
  max-height: calc(85vh - 100px);
  min-width: 0;

  @media (max-width: 768px) {
    padding: 12px;
    border-right: none;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    max-height: none;
    overflow-y: visible;
  }
`;

const SectionTitle = styled.h3`
  font-size: 0.8em;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.6);
  text-transform: uppercase;
  letter-spacing: 0.1em;
  margin: 0 0 12px 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const LinesGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

// ============ LINE CARD ============

const LineCard = styled.div`
  background: var(--al-surface);
  border: 1px solid rgba(255, 255, 255, 0.1);
  display: grid;
  grid-template-columns: 80px 1fr auto;
  gap: 12px;
  padding: 10px;
  transition: border-color 0.2s;
  
  &:hover {
    border-color: rgba(var(--al-primary-rgb), 0.3);
  }

  @media (max-width: 768px) {
    grid-template-columns: 56px 1fr auto;
    gap: 10px;
    padding: 10px;
  }
`;

const LineThumbnail = styled.div`
  width: 80px;
  height: 80px;
  background: #363636;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  overflow: hidden;
  flex-shrink: 0;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
  
  &:hover {
    opacity: 0.9;
  }

  @media (max-width: 768px) {
    width: 56px;
    height: 56px;
  }
`;

const LineDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
`;

const LineTitle = styled.div`
  font-weight: 600;
  font-size: 1em;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  overflow-wrap: anywhere;

  @media (max-width: 768px) {
    font-size: 0.9em;
    line-height: 1.2;
  }
`;

const LineSubtitle = styled.div`
  font-size: 0.8em;
  color: rgba(255, 255, 255, 0.6);
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  
  span {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  @media (max-width: 768px) {
    gap: 6px;
    font-size: 0.75em;
  }
`;

const LineSizes = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 4px;
`;

const SizeChip = styled.span`
  background: rgba(255, 255, 255, 0.1);
  padding: 2px 8px;
  font-size: 0.7em;
  color: rgba(255, 255, 255, 0.8);
  white-space: nowrap;
  
  .qty {
    color: var(--al-primary);
    font-weight: 600;
    margin-left: 4px;
  }
`;

const LineStatusFlags = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: auto;
  flex-wrap: wrap;
`;

const LineTypeLabel = styled.span`
  font-size: 0.65em;
  font-weight: 600;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.5);
  letter-spacing: 0.03em;
  margin-right: 4px;
  flex-shrink: 0;
`;

const MiniFlag = styled.span`
  padding: 2px 6px;
  font-size: 0.65em;
  font-weight: 600;
  text-transform: uppercase;
  background: ${props => props.active ? 'rgba(var(--al-primary-rgb), 0.2)' : 'transparent'};
  color: ${props => props.active ? 'var(--al-primary)' : 'rgba(255, 255, 255, 0.3)'};
  border: 1px solid ${props => props.active ? 'rgba(var(--al-primary-rgb), 0.4)' : 'rgba(255, 255, 255, 0.1)'};
`;

const LineActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  justify-content: center;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 6px;
    justify-content: center;
    align-items: stretch;
  }
`;

const SmallActionButton = styled.button`
  background: ${props => props.primary ? 'var(--al-primary)' : 'rgba(255, 255, 255, 0.1)'};
  color: ${props => props.primary ? 'var(--al-bg)' : 'white'};
  border: none;
  padding: 6px 12px;
  font-size: 0.7em;
  font-weight: 600;
  text-transform: uppercase;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s;
  white-space: nowrap;
  
  &:hover {
    background: ${props => props.primary ? '#7FFF7F' : 'rgba(var(--al-primary-rgb), 0.2)'};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  /* "Files" is hidden on mobile to keep cards narrow; sticker stays as a small icon button */
  &.mobile-hide {
    @media (max-width: 768px) {
      display: none;
    }
  }

  @media (max-width: 768px) {
    width: 32px;
    height: 32px;
    padding: 0;
    justify-content: center;
    align-items: center;
    border-radius: 4px;

    & > svg {
      font-size: 14px;
    }

    .action-label {
      display: none;
    }
  }
`;

// ============ SIDEBAR ============

const Sidebar = styled.div`
  background: var(--al-surface);
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  overflow-y: auto;
  max-height: calc(85vh - 100px);
  min-width: 0;

  @media (max-width: 768px) {
    padding: 16px;
    max-height: none;
    overflow-y: visible;
    gap: 16px;
  }
`;

const SummaryCard = styled.div`
  background: rgba(0, 0, 0, 0.3);
  padding: 16px;
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  
  &:last-child {
    border-bottom: none;
  }
  
  &.total {
    border-top: 2px solid rgba(255, 255, 255, 0.2);
    border-bottom: none;
    margin-top: 8px;
    padding-top: 16px;
  }
`;

const SummaryLabel = styled.span`
  font-size: 0.8em;
  color: rgba(255, 255, 255, 0.6);
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const SummaryValue = styled.span`
  font-size: ${props => props.large ? '1.3em' : '0.95em'};
  font-weight: ${props => props.large ? '700' : '500'};
  color: ${props => props.discount ? '#dc3545' : props.highlight ? 'var(--al-primary)' : 'white'};
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 12px;
  }
`;

const InfoItem = styled.div`
  .label {
    font-size: 0.7em;
    color: rgba(255, 255, 255, 0.5);
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin-bottom: 4px;
  }
  
  .value {
    font-size: 0.9em;
    color: white;
  }
`;

const NotesBox = styled.div`
  background: rgba(0, 0, 0, 0.3);
  padding: 16px;
  
  .notes-label {
    font-size: 0.7em;
    color: rgba(255, 255, 255, 0.5);
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin-bottom: 8px;
  }
  
  .notes-content {
    font-size: 0.85em;
    color: rgba(255, 255, 255, 0.8);
    white-space: pre-wrap;
    line-height: 1.5;
    text-transform: none;
  }
`;

const ActionsSection = styled.div`
  margin-top: auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ShippingCard = styled(SummaryCard)`
  .tracking-link {
    color: var(--al-primary);
    text-transform: none;
    text-decoration: underline;
    cursor: pointer;
    font-size: 0.85em;
  }
`;

const ActionButton = styled.button`
  background: ${props => props.primary ? 'var(--al-primary)' : 'rgba(255, 255, 255, 0.1)'};
  color: ${props => props.primary ? 'var(--al-bg)' : 'white'};
  border: none;
  padding: 14px 20px;
  font-size: 0.85em;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  transition: all 0.2s;
  
  &:hover {
    background: ${props => props.primary ? '#7FFF7F' : 'rgba(var(--al-primary-rgb), 0.2)'};
  }
`;

// ============ ARTWORK MODAL ============

const LargeArtworkModal = styled(ModalOverlay)`
  background-color: rgba(0, 0, 0, 0.95);
`;

const LargeArtworkContent = styled.div`
  width: 95vw;
  height: 95vh;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
`;

const LargeArtworkImage = styled.img`
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
`;

// ============ ACTIVITY LOG MODAL ============

const HistoryModalContent = styled.div`
  background: var(--al-bg);
  width: 95%;
  max-width: 560px;
  max-height: 80vh;
  position: relative;
  font-family: 'Montserrat', sans-serif;
  color: white;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const HistoryHeader = styled.div`
  background: var(--al-surface);
  padding: 16px 24px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  flex-shrink: 0;

  .title {
    font-size: 1em;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .subtitle {
    font-size: 0.8em;
    color: rgba(255, 255, 255, 0.6);
    margin-top: 4px;
  }
`;

const HistoryBody = styled.div`
  padding: 20px 24px;
  overflow-y: auto;
`;

const TimelineItem = styled.div`
  position: relative;
  padding: 0 0 18px 22px;
  border-left: 2px solid rgba(var(--al-primary-rgb), 0.25);

  &:last-child {
    border-left-color: transparent;
    padding-bottom: 0;
  }

  &::before {
    content: '';
    position: absolute;
    left: -7px;
    top: 2px;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: var(--al-primary);
    border: 2px solid var(--al-bg);
  }

  .event {
    font-weight: 600;
    font-size: 0.9em;
  }

  .meta {
    font-size: 0.78em;
    color: rgba(255, 255, 255, 0.6);
    margin-top: 2px;
  }

  .transition {
    font-size: 0.72em;
    color: rgba(255, 255, 255, 0.45);
    margin-top: 2px;
    text-transform: uppercase;
    letter-spacing: 0.03em;
  }

  .details {
    font-size: 0.78em;
    color: rgba(var(--al-primary-rgb), 0.8);
    margin-top: 2px;
  }
`;

const EmptyHistory = styled.div`
  font-size: 0.85em;
  color: rgba(255, 255, 255, 0.5);
  text-align: center;
  padding: 24px 0;
  line-height: 1.5;
`;

// ============ COMPONENT ============

const OrderView = ({ order, onClose }) => {
  const [selectedArtwork, setSelectedArtwork] = useState(null);
  const [printingLines, setPrintingLines] = useState(new Set());
  const [printSuccessLines, setPrintSuccessLines] = useState(new Set());
  const [jobFilesModal, setJobFilesModal] = useState({ open: false, orderNumber: '', designName: '', lineId: null });
  const [stickerToPrint, setStickerToPrint] = useState(null);
  const [boxDialogOpen, setBoxDialogOpen] = useState(false);
  const [boxToPrint, setBoxToPrint] = useState(null);
  const [currentBoxPrinting, setCurrentBoxPrinting] = useState(null);
  const [customerEmail, setCustomerEmail] = useState(order?.contactEmail || '');
  const [customerTaxExempt, setCustomerTaxExempt] = useState(false);
  const [sendingTrackingEmail, setSendingTrackingEmail] = useState(false);
  const [historyLine, setHistoryLine] = useState(null);
  const [deliveredToHomeAt, setDeliveredToHomeAt] = useState(order?.deliveredToHomeAt || null);
  const [markingDelivered, setMarkingDelivered] = useState(false);
  const printingRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    const loadCustomerEmail = async () => {
      if (order?.contactEmail) {
        setCustomerEmail(order.contactEmail);
        return;
      }
      if (!order?.customerId) return;
      try {
        const customerSnap = await getDoc(doc(db, 'customers', order.customerId));
        if (!cancelled && customerSnap.exists()) {
          const data = customerSnap.data();
          setCustomerEmail(data?.email || '');
          setCustomerTaxExempt(Boolean(data?.taxExempt));
        }
      } catch (error) {
        console.error('Failed to load customer email for tracking:', error);
      }
    };
    loadCustomerEmail();
    return () => { cancelled = true; };
  }, [order?.customerId, order?.contactEmail]);

  if (!order) return null;

  // Format an ISO timestamp from the activity log into a friendly date + time
  const formatLogTime = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return String(iso);
    return d.toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit'
    });
  };

  // Build a chronological list of activity entries for a line.
  // Lines created before logging existed fall back to a synthesized "Created" entry.
  const getLineHistory = (line) => {
    const entries = Array.isArray(line?.activityLog) ? [...line.activityLog] : [];
    if (entries.length === 0 && line?.createdAt) {
      entries.push({ event: 'Created', _fallbackDate: line.createdAt });
    }
    return entries.sort((a, b) => {
      const ta = a.at ? new Date(a.at).getTime() : 0;
      const tb = b.at ? new Date(b.at).getTime() : 0;
      return ta - tb;
    });
  };

  const getLineTypeDisplay = (line) => {
    const t = String(line?.type || '').toLowerCase();
    if (t === 'screen' || t === 'screen print') return LINE_TYPES.SCREEN_PRINT;
    if (t === 'embroidery') return LINE_TYPES.EMBROIDERY;
    if (t === 'dtg') return LINE_TYPES.DTG;
    if (t === 'dtf') return LINE_TYPES.DTF;
    if (t === 'design_only' || t === 'design only') return LINE_TYPES.DESIGN_ONLY;
    if (Object.values(LINE_TYPES).includes(line?.type)) return line.type;
    return line?.type || LINE_TYPES.SCREEN_PRINT;
  };

  const {
    subtotal,
    feesTotal,
    discountAmount,
    tax,
    total,
    upchargeTotal,
    isTaxExempt,
    orderFees
  } = computeOrderDocumentTotals(order, order.lines || [], {
    customer: customerTaxExempt ? { taxExempt: true } : null,
    invoiceTaxExempt: order.invoiceTaxExempt
  });
  
  const totalPieces = order.lines?.reduce((sum, line) => {
    const lineQty = Object.values(line.sizes || {}).reduce((s, qty) => s + (Number(qty) || 0), 0);
    return sum + lineQty;
  }, 0) || 0;

  const shippingCost = Number(order?.shippingDetails?.totalShipmentCost || 0);
  const trackingNumber = order?.shippingDetails?.trackingNumber || '';
  const labelDownloadUrl = order?.shippingDetails?.labelDownloadUrl || '';
  const carrierLabel = order?.shippingDetails?.preferredRate?.carrierCode || 'UPS';
  const trackingUrl = trackingNumber
    ? `https://www.ups.com/track?tracknum=${encodeURIComponent(trackingNumber)}`
    : '';

  const handleSendTrackingEmail = async () => {
    if (!customerEmail || !trackingNumber || sendingTrackingEmail) return;
    try {
      setSendingTrackingEmail(true);
      const orderNumber = order.poNumber || order.orderNumber || order.id;
      const content = getEmailContent('order_shipped', {
        customerName: order.customerName || order.client || 'Customer',
        orderNumber,
        carrier: carrierLabel.toUpperCase(),
        trackingNumber,
        trackingUrl
      });
      const sendEmail = httpsCallable(functions, 'sendEmail');
      await sendEmail({
        to: customerEmail,
        subject: content.subject,
        text: content.text,
        html: content.html
      });
      await updateDoc(doc(db, 'orders', order.id), {
        shippingTrackingEmailSentAt: serverTimestamp(),
        shippingTrackingEmailRecipient: customerEmail,
        updatedAt: serverTimestamp()
      });
      alert('Tracking email sent.');
    } catch (error) {
      console.error('Failed to send tracking email:', error);
      alert(`Failed to send tracking email: ${error.message || 'Unknown error'}`);
    } finally {
      setSendingTrackingEmail(false);
    }
  };

  // Orders produced away from home (e.g. SP order printed at 87C) only get the
  // ready-for-pickup email once they're marked delivered back to the home facility.
  const awayFromHome = isAwayFromHome(order);
  const homeFacility = getFacility(resolveHomeFacilityId(order));
  const productionFacility = getFacility(resolveFacilityId(order?.facilityId));
  const productionDone =
    order?.status === ORDER_STATUSES.READY_TO_INVOICE || order?.status === ORDER_STATUSES.COMPLETED;

  const handleMarkDeliveredToHome = async () => {
    if (markingDelivered || deliveredToHomeAt) return;
    const ok = window.confirm(
      `Confirm this order is physically at ${homeFacility.label}?\n\nThe customer will get the ready-for-pickup email.`
    );
    if (!ok) return;
    try {
      setMarkingDelivered(true);
      await updateDoc(doc(db, 'orders', order.id), {
        deliveredToHomeAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      setDeliveredToHomeAt(new Date());
    } catch (error) {
      console.error('Failed to mark delivered:', error);
      alert(`Failed to mark delivered: ${error.message || 'Unknown error'}`);
    } finally {
      setMarkingDelivered(false);
    }
  };

  // ============ HANDLERS ============

  const handlePrintLine = async (line) => {
    if (printingLines.has(line.id)) return;
    
    setPrintingLines(prev => new Set([...prev, line.id]));
    setPrintSuccessLines(prev => {
      const newSet = new Set(prev);
      newSet.delete(line.id);
      return newSet;
    });

    try {
      const { line: freshLine, order: freshOrder } = await fetchStickerPrintPayload(
        db,
        line.id,
        line.orderId || order?.id
      );
      setStickerToPrint({ line: freshLine, order: freshOrder || order });
    } catch (error) {
      console.error('Error loading sticker data:', error);
      setPrintingLines(prev => {
        const next = new Set(prev);
        next.delete(line.id);
        return next;
      });
      setStickerToPrint({ line, order });
    }
  };

  const openJobFilesModal = (orderNumber, designName, lineId) => {
    setJobFilesModal({ open: true, orderNumber, designName, lineId });
  };

  const handleBoxLabelsClick = () => {
    setBoxDialogOpen(true);
  };

  const handlePrintBoxes = async (totalBoxes) => {
    if (printingRef.current) return;

    try {
      printingRef.current = true;
      setBoxDialogOpen(false);
      
      for (let boxNumber = 1; boxNumber <= totalBoxes; boxNumber++) {
        setCurrentBoxPrinting({ currentBox: boxNumber, totalBoxes });
        
        const printedSuccessfully = await new Promise((resolve) => {
          setBoxToPrint({
            order,
            currentBox: boxNumber,
            totalBoxes,
            onComplete: (success) => resolve(success)
          });
        });

        setBoxToPrint(null);

        // Stop batch printing after the first failed label so user can retry safely.
        if (!printedSuccessfully) {
          break;
        }
      }
    } catch (error) {
      console.error('Error during print sequence:', error);
    } finally {
      setCurrentBoxPrinting(null);
      printingRef.current = false;
      setBoxToPrint(null);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Created': return <FaClock />;
      case 'Ready To Order': return <FaClipboardCheck />;
      case 'Ordered': return <FaTruck />;
      case 'Provided Goods': return <FaTruck />;
      case 'Received': return <FaBox />;
      case 'Ready To Invoice': return <FaFileInvoiceDollar />;
      case 'Completed': return <FaCheckCircle />;
      default: return null;
    }
  };
  const statusDisplay = getOrderStatusDisplay(order);

  // ============ RENDER ============

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={e => e.stopPropagation()}>
        <CloseButton onClick={onClose}>
          <FaTimes />
        </CloseButton>

        {/* Header */}
        <Header>
          <HeaderLeft>
            <OrderNumber>
              <div className="label">Order No.</div>
              <div className="number">{formatOrderPoNumber(order)}</div>
            </OrderNumber>
            <CustomerInfo>
              <div className="customer-name">{order.customerName || order.client || 'Unknown Customer'}</div>
              {order.contact && <div className="contact">{order.contact}</div>}
            </CustomerInfo>
          </HeaderLeft>
          <HeaderRight>
            <StatusBadge status={order.status}>
              {getStatusIcon(statusDisplay)}
              {statusDisplay}
            </StatusBadge>
            <DueDate>Due: <span>{formatDate(order.dueDate)}</span></DueDate>
          </HeaderRight>
        </Header>

        {/* Main Content */}
        <MainLayout>
          {/* Lines Section */}
          <LinesSection>
            <SectionTitle>
              <FaTag /> Order Lines ({order.lines?.length || 0})
            </SectionTitle>
            <LinesGrid>
              {order.lines?.map((line, index) => (
                <LineCard key={line.id || index}>
                  <LineThumbnail onClick={() => {
                    const mockupUrl = getMockupUrl(line);
                    if (mockupUrl) setSelectedArtwork(mockupUrl);
                  }}>
                    {getMockupUrl(line) ? (
                      <img src={getMockupUrl(line)} alt={line.designName} />
                    ) : (
                      <span style={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.7em' }}>NO IMAGE</span>
                    )}
                  </LineThumbnail>
                  
                  <LineDetails>
                    <LineTitle>{line.designName || 'Untitled Design'}</LineTitle>
                    <LineSubtitle>
                      <span>{line.garmentId?.split(' - ')[0] || 'No Style'}</span>
                      <span>•</span>
                      <span>{line.garmentColor || 'No Color'}</span>
                      <span>•</span>
                      <span style={{ color: 'var(--al-primary)' }}>
                        {Object.values(line.sizes || {}).reduce((s, q) => s + (Number(q) || 0), 0)} pcs
                      </span>
                    </LineSubtitle>
                    
                    <LineSizes>
                      {Object.entries(line.sizes || {})
                        .filter(([_, qty]) => Number(qty) > 0)
                        .sort(([a], [b]) => sortSizes(a, b))
                        .map(([size, qty]) => (
                          <SizeChip key={size}>
                            {size}<span className="qty">×{qty}</span>
                          </SizeChip>
                        ))}
                    </LineSizes>
                    
                    <LineStatusFlags>
                      <LineTypeLabel>{getLineTypeDisplay(line)}</LineTypeLabel>
                      <MiniFlag active={line.isIn}>IN</MiniFlag>
                      <MiniFlag active={line.isCut}>CUT</MiniFlag>
                      <MiniFlag active={line.printed}>PRINTED</MiniFlag>
                    </LineStatusFlags>
                  </LineDetails>
                  
                  <LineActions>
                    <SmallActionButton
                      className="mobile-hide"
                      onClick={() => openJobFilesModal(order.poNumber || order.orderNumber, line.designName, line.id)}
                      title="Open files"
                    >
                      <FaFolderOpen /> Files
                    </SmallActionButton>
                    <SmallActionButton
                      primary
                      onClick={() => handlePrintLine(line)}
                      disabled={printingLines.has(line.id)}
                      title={printingLines.has(line.id) ? 'Printing sticker' : 'Print sticker'}
                    >
                      <FaTag />
                      <span className="action-label">
                        {printingLines.has(line.id) ? 'Printing...' : 'Sticker'}
                        {printSuccessLines.has(line.id) && ' ✓'}
                      </span>
                    </SmallActionButton>
                    <SmallActionButton
                      onClick={() => setHistoryLine(line)}
                      title="View activity log"
                    >
                      <FaHistory />
                      <span className="action-label">Timestamp</span>
                    </SmallActionButton>
                  </LineActions>
                </LineCard>
              ))}
            </LinesGrid>
          </LinesSection>

          {/* Sidebar */}
          <Sidebar>
            <SectionTitle>
              <FaFileInvoiceDollar /> Order Summary
            </SectionTitle>
            
            <SummaryCard>
              <SummaryRow>
                <SummaryLabel>Total Lines</SummaryLabel>
                <SummaryValue>{order.lines?.length || 0}</SummaryValue>
              </SummaryRow>
              <SummaryRow>
                <SummaryLabel>Total Pieces</SummaryLabel>
                <SummaryValue highlight>{totalPieces.toLocaleString()}</SummaryValue>
              </SummaryRow>
              <SummaryRow>
                <SummaryLabel>Subtotal</SummaryLabel>
                <SummaryValue>${subtotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</SummaryValue>
              </SummaryRow>
              {upchargeTotal > 0 && (
                <SummaryRow>
                  <SummaryLabel style={{ fontSize: '0.85em', color: '#999' }}>Includes 2XL+ Upcharge</SummaryLabel>
                  <SummaryValue style={{ fontSize: '0.85em', color: '#999' }}>
                    ${upchargeTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </SummaryValue>
                </SummaryRow>
              )}
              {orderFees.length > 0 && (
                <SummaryRow>
                  <SummaryLabel>Fees</SummaryLabel>
                  <SummaryValue>${feesTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</SummaryValue>
                </SummaryRow>
              )}
              {discountAmount > 0 && (
                <SummaryRow>
                  <SummaryLabel>
                    Discount {order.discount?.type === 'percent' ? `(${order.discount.amount}%)` : ''}
                  </SummaryLabel>
                  <SummaryValue discount>-${discountAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</SummaryValue>
                </SummaryRow>
              )}
              <SummaryRow>
                <SummaryLabel>{isTaxExempt ? 'Tax' : 'Tax (7%)'}</SummaryLabel>
                <SummaryValue
                  style={isTaxExempt ? { color: '#64B5F6', fontWeight: 600 } : undefined}
                >
                  {isTaxExempt
                    ? 'Tax Exempt'
                    : `$${tax.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                </SummaryValue>
              </SummaryRow>
              <SummaryRow className="total">
                <SummaryLabel style={{ fontWeight: 600, color: 'white' }}>Total</SummaryLabel>
                <SummaryValue large highlight>${total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</SummaryValue>
              </SummaryRow>
            </SummaryCard>

            {(trackingNumber || shippingCost > 0) && (
              <ShippingCard>
                <SectionTitle style={{ marginBottom: 10 }}>
                  <FaTruck /> Shipping
                </SectionTitle>
                <SummaryRow>
                  <SummaryLabel>Label Cost</SummaryLabel>
                  <SummaryValue>${shippingCost.toFixed(2)}</SummaryValue>
                </SummaryRow>
                <SummaryRow>
                  <SummaryLabel>Tracking</SummaryLabel>
                  <SummaryValue>{trackingNumber || 'Pending'}</SummaryValue>
                </SummaryRow>
                {trackingUrl && (
                  <SummaryRow>
                    <SummaryLabel>Link</SummaryLabel>
                    <span className="tracking-link" onClick={() => window.open(trackingUrl, '_blank', 'noopener,noreferrer')}>
                      Open Tracking
                    </span>
                  </SummaryRow>
                )}
                {labelDownloadUrl && (
                  <SummaryRow>
                    <SummaryLabel>Label</SummaryLabel>
                    <span className="tracking-link" onClick={() => window.open(labelDownloadUrl, '_blank', 'noopener,noreferrer')}>
                      Download Label
                    </span>
                  </SummaryRow>
                )}
                {trackingNumber && (
                  <ActionButton
                    primary
                    onClick={handleSendTrackingEmail}
                    disabled={!customerEmail || sendingTrackingEmail}
                    title={customerEmail ? 'Email tracking to customer' : 'Customer email missing'}
                  >
                    {sendingTrackingEmail ? 'Sending...' : 'Email Tracking'}
                  </ActionButton>
                )}
              </ShippingCard>
            )}

            <InfoGrid>
              <InfoItem>
                <div className="label">Project Manager</div>
                <div className="value">{order.projectManager || 'N/A'}</div>
              </InfoItem>
              <InfoItem>
                <div className="label">Created</div>
                <div className="value">{formatDate(order.createdAt)}</div>
              </InfoItem>
              {awayFromHome && (
                <InfoItem>
                  <div className="label">Made at → Pickup</div>
                  <div className="value">{productionFacility.shortLabel} → {homeFacility.shortLabel}</div>
                </InfoItem>
              )}
              {awayFromHome && deliveredToHomeAt && (
                <InfoItem>
                  <div className="label">Delivered to {homeFacility.shortLabel}</div>
                  <div className="value">{formatDate(deliveredToHomeAt)}</div>
                </InfoItem>
              )}
            </InfoGrid>

            {order.notes && (
              <NotesBox>
                <div className="notes-label">Order Notes</div>
                <div className="notes-content">{order.notes}</div>
              </NotesBox>
            )}

            <ActionsSection>
              <ActionButton primary onClick={handleBoxLabelsClick}>
                <FaBox /> Print Box Labels
              </ActionButton>
              {awayFromHome && productionDone && !deliveredToHomeAt && (
                <ActionButton
                  primary
                  onClick={handleMarkDeliveredToHome}
                  disabled={markingDelivered}
                  title={`Sends the customer the ready-for-pickup email from ${homeFacility.label}`}
                >
                  <FaTruck /> {markingDelivered ? 'Saving...' : `Mark Delivered to ${homeFacility.shortLabel}`}
                </ActionButton>
              )}
            </ActionsSection>
          </Sidebar>
        </MainLayout>

        {/* Hidden sticker printing */}
        {stickerToPrint && (
          <div style={{ display: 'none' }}>
            <JobSticker
              line={stickerToPrint.line}
              order={stickerToPrint.order}
              onPdfGenerated={async (pdfData) => {
                if (!pdfData) return;
                try {
                  await printLabelBase64(pdfData, { type: 'line', tray: 1 });

                  setPrintSuccessLines(prev => new Set([...prev, stickerToPrint.line.id]));
                  setTimeout(() => {
                    setPrintSuccessLines(prev => {
                      const newSet = new Set(prev);
                      newSet.delete(stickerToPrint.line.id);
                      return newSet;
                    });
                  }, 3000);
                } catch (error) {
                  console.error('Print error:', error);
                  alert(`Failed to print: ${error.message}`);
                } finally {
                  setPrintingLines(prev => {
                    const newSet = new Set(prev);
                    newSet.delete(stickerToPrint.line.id);
                    return newSet;
                  });
                  setStickerToPrint(null);
                }
              }}
            />
          </div>
        )}

        {/* Box dialog */}
        {boxDialogOpen && (
          <BoxCountDialog
            open={boxDialogOpen}
            onClose={() => setBoxDialogOpen(false)}
            onConfirm={handlePrintBoxes}
            orderDetails={{
              orderNumber: order.poNumber || order.orderNumber,
              client: order.customerName,
              lines: order.lines
            }}
          />
        )}

        {/* Hidden box sticker printing */}
        {boxToPrint && (
          <div style={{ display: 'none' }}>
            <BoxSticker
              invoiceNumber={order.poNumber || order.orderNumber}
              client={order.customerName}
              total={total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              manager={order.projectManager}
              pieces={totalPieces}
              currentBox={boxToPrint.currentBox}
              totalBoxes={boxToPrint.totalBoxes}
              onPdfGenerated={async (pdfData) => {
                if (!pdfData) return;
                let printedSuccessfully = false;
                try {
                  await printLabelBase64(pdfData, { type: 'box' });

                  printedSuccessfully = true;
                } catch (error) {
                  console.error('Box label print error:', error);
                  alert(`Failed to print box label: ${error.message}`);
                } finally {
                  if (boxToPrint?.onComplete) boxToPrint.onComplete(printedSuccessfully);
                }
              }}
            />
          </div>
        )}

        {/* Print progress indicator */}
        {currentBoxPrinting && (
          <div style={{ 
            position: 'fixed', 
            bottom: 20, 
            right: 20, 
            background: 'var(--al-surface)', 
            color: 'white', 
            padding: '12px 20px', 
            zIndex: 9999,
            border: '1px solid var(--al-primary)',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            <FaPrint style={{ color: 'var(--al-primary)' }} />
            Printing box {currentBoxPrinting.currentBox} of {currentBoxPrinting.totalBoxes}...
          </div>
        )}
      </ModalContent>

      {/* Large artwork preview */}
      {selectedArtwork && (
        <LargeArtworkModal onClick={() => setSelectedArtwork(null)}>
          <LargeArtworkContent onClick={e => e.stopPropagation()}>
            <CloseButton onClick={() => setSelectedArtwork(null)} style={{ top: 10, right: 10 }}>
              <FaTimes />
            </CloseButton>
            <LargeArtworkImage src={selectedArtwork} alt="Large artwork preview" />
          </LargeArtworkContent>
        </LargeArtworkModal>
      )}

      {/* Files Modal */}
      <FilesModal
        isOpen={jobFilesModal.open}
        onClose={() => setJobFilesModal({ open: false, orderNumber: '', designName: '', lineId: null })}
        orderNumber={jobFilesModal.orderNumber}
        designName={jobFilesModal.designName}
        lineId={jobFilesModal.lineId}
      />

      {/* Line activity log */}
      {historyLine && (
        <ModalOverlay
          style={{ zIndex: 1100 }}
          onClick={(e) => { e.stopPropagation(); setHistoryLine(null); }}
        >
          <HistoryModalContent onClick={e => e.stopPropagation()}>
            <CloseButton onClick={() => setHistoryLine(null)}>
              <FaTimes />
            </CloseButton>
            <HistoryHeader>
              <div className="title"><FaHistory /> Activity Log</div>
              <div className="subtitle">{historyLine.designName || 'Untitled Design'}</div>
            </HistoryHeader>
            <HistoryBody>
              {getLineHistory(historyLine).length === 0 ? (
                <EmptyHistory>
                  No activity recorded yet for this line.<br />
                  New changes will appear here going forward.
                </EmptyHistory>
              ) : (
                getLineHistory(historyLine).map((entry, i) => (
                  <TimelineItem key={i}>
                    <div className="event">{entry.event}</div>
                    <div className="meta">
                      {entry.at ? formatLogTime(entry.at) : (entry._fallbackDate ? formatDate(entry._fallbackDate) : 'Date unknown')}
                      {entry.by ? ` · ${entry.by}` : ''}
                    </div>
                    {(entry.from || entry.to) && (
                      <div className="transition">{entry.from || '—'} → {entry.to || '—'}</div>
                    )}
                    {entry.details && <div className="details">{entry.details}</div>}
                  </TimelineItem>
                ))
              )}
            </HistoryBody>
          </HistoryModalContent>
        </ModalOverlay>
      )}
    </ModalOverlay>
  );
};

export default OrderView;
